import { useMediaQuery } from '../../hooks/useMediaQuery.js';
import { useShellSurface } from './shellSurface.js';

/* ===========================================================================
 * AT WHICH WIDTHS THE COLLAPSED (ICON) RAIL APPLIES.
 *
 * The collapsed styles used to be `md:`-only, because the one narrow layout
 * anybody had drawn was the worker's phone DRAWER, which is always full width.
 * The back office has no drawer (shellSurface.ts — the owner's call: no
 * hamburger, the rail stays docked), so below md it was simply the full 252px
 * rail with the page squeezed beside it — about 120px of content on a phone.
 *
 * So on the back office the rail is forced to ICONS below md — still docked,
 * still the console the reader was using — and its collapsed styles apply at
 * EVERY width rather than only md+. The reader's saved collapse preference is
 * not touched; it still decides md and up.
 * ======================================================================== */

/** True when collapsed styles apply at every width: the back office, which has no drawer. */
export function useRailEveryWidth(): boolean {
  return !useShellSurface().phoneShaped;
}

/** The rail's effective mode: `iconOnly` is what the rows should render. */
export function useRailMode(collapsed: boolean) {
  const everyWidth = useRailEveryWidth();
  // Tailwind's own `max-md` query. Asked as 'narrow?', not 'md?', so a browser
  // (or test) that cannot answer falls back to the full rail it always had.
  const narrow = useMediaQuery('not all and (min-width: 768px)');
  return { everyWidth, iconOnly: collapsed || (everyWidth && narrow), cc: collapsedClasses(everyWidth) };
}

/** Both spellings written out in full, so Tailwind sees every class it must generate. */
export function collapsedClasses(everyWidth: boolean) {
  return everyWidth
    ? {
        row: 'justify-center ps-0 pe-0',
        label: 'sr-only',
        inset: 'ps-1 pe-1',
        hide: 'hidden',
        center: 'justify-center',
      }
    : {
        row: 'md:justify-center md:ps-0 md:pe-0',
        label: 'md:sr-only',
        inset: 'md:ps-1 md:pe-1',
        hide: 'md:hidden',
        center: 'md:justify-center',
      };
}
